import { useMemo, useState } from 'react';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { FILTER_FORMAT_MAP } from '../../engine/filterRegistry';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import { FilterId, type RangeFilter } from '../../types/filters';
import layout from './filterFormLayout.module.css';
import { SubFilterInputForm } from './SubFilterInputForm';

interface OperatorOption {
  value: 'between' | 'greater_than' | 'less_than';
  label: string;
}

interface SegmentedRangeFilterFormProps {
  filter: RangeFilter;
  closeMenu: () => void;
  onSave: (filter: RangeFilter) => void;
}

const operatorOptions: [OperatorOption, OperatorOption, OperatorOption] = [
  { value: 'between', label: 'is between' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
];

const getDefaultValues = (
  filterId: FilterId
): { startValue: number; endValue: number } | null => {
  switch (filterId) {
    case FilterId.ATTRITION_RATE:
    case FilterId.GROWTH_RATE:
    case FilterId.HIRING_RATE:
      return { startValue: 0, endValue: 25 };
    case FilterId.HEADCOUNT:
      return { startValue: 0, endValue: 10000 };
    case FilterId.SALARY:
      return { startValue: 0, endValue: 100000 };
    case FilterId.TENURE:
      return { startValue: 0, endValue: 2 };
    case FilterId.INFLOW:
    case FilterId.OUTFLOW:
      return { startValue: 0, endValue: 100 };
    default:
      return null;
  }
};

const getInitialOperator = (
  min: number | undefined,
  max: number | undefined
): OperatorOption => {
  if (min !== undefined && max !== undefined) {
    return operatorOptions[0];
  } else if (min !== undefined) {
    return operatorOptions[1];
  } else if (max !== undefined) {
    return operatorOptions[2];
  }
  return operatorOptions[0];
};

export const SegmentedRangeFilterForm = ({
  filter,
  closeMenu,
  onSave,
}: SegmentedRangeFilterFormProps) => {
  const { exchangeRate } = useCurrencyPreference();
  const trackFilterReset = useTrackFilterReset();
  const format = FILTER_FORMAT_MAP[filter.id];
  const isCurrencyFilter = format?.text === '$' && format?.type === 'prefix';
  const isFundingFilter =
    filter.id === FilterId.LAST_FUNDING_AMOUNT ||
    filter.id === FilterId.TOTAL_FUNDING_AMOUNT;
  const isPayFilter = filter.id === FilterId.SALARY;
  const defaults = getDefaultValues(filter.id);

  const [operator, setOperator] = useState<OperatorOption>(() =>
    getInitialOperator(filter.min, filter.max)
  );
  const [startValue, setStartValue] = useState<number | undefined>(() => {
    if (isCurrencyFilter && filter.min !== undefined) {
      return filter.min * exchangeRate;
    }
    return filter.min;
  });
  const [endValue, setEndValue] = useState<number | undefined>(() => {
    if (isCurrencyFilter && filter.max !== undefined) {
      return filter.max * exchangeRate;
    }
    return filter.max;
  });
  const [showErrors, setShowErrors] = useState(false);

  const errorMessages = useMemo(() => {
    const errors: string[] = [];

    if (operator.value === 'between') {
      if (startValue === undefined) {
        errors.push('Start value is required.');
      }
      if (endValue === undefined) {
        errors.push('End value is required.');
      }
      if (
        startValue !== undefined &&
        endValue !== undefined &&
        startValue >= endValue
      ) {
        errors.push('Start value must be less than the end value.');
      }
    } else if (operator.value === 'greater_than') {
      if (startValue === undefined) {
        errors.push('Value is required.');
      }
    } else if (operator.value === 'less_than') {
      if (endValue === undefined) {
        errors.push('Value is required.');
      }
    }

    return errors;
  }, [operator.value, startValue, endValue]);

  const handleOperatorChange = (nextOperator: OperatorOption) => {
    const previousOperator = operator.value;
    setOperator(nextOperator);

    if (nextOperator.value === 'greater_than') {
      if (previousOperator === 'between') {
        setEndValue(undefined);
      } else if (defaults && startValue === undefined) {
        const defaultStart = isCurrencyFilter
          ? defaults.startValue * exchangeRate
          : defaults.startValue;
        setStartValue(defaultStart);
      }
    } else if (nextOperator.value === 'less_than') {
      if (previousOperator === 'between') {
        setStartValue(undefined);
      } else if (defaults && endValue === undefined) {
        const defaultEnd = isCurrencyFilter
          ? defaults.endValue * exchangeRate
          : defaults.endValue;
        setEndValue(defaultEnd);
      }
    } else if (nextOperator.value === 'between') {
      if (previousOperator === 'greater_than' && startValue !== undefined) {
        if (defaults && endValue === undefined) {
          const defaultEnd = isCurrencyFilter
            ? defaults.endValue * exchangeRate
            : defaults.endValue;
          setEndValue(defaultEnd);
        }
      } else if (previousOperator === 'less_than' && endValue !== undefined) {
        if (defaults && startValue === undefined) {
          const defaultStart = isCurrencyFilter
            ? defaults.startValue * exchangeRate
            : defaults.startValue;
          setStartValue(defaultStart);
        }
      } else if (
        startValue === undefined &&
        endValue === undefined &&
        defaults
      ) {
        const defaultStart = isCurrencyFilter
          ? defaults.startValue * exchangeRate
          : defaults.startValue;
        const defaultEnd = isCurrencyFilter
          ? defaults.endValue * exchangeRate
          : defaults.endValue;
        setStartValue(defaultStart);
        setEndValue(defaultEnd);
      }
    }

    setShowErrors(false);
  };

  const isAddDisabled = (() => {
    if (operator.value === 'greater_than' && startValue === undefined) {
      return true;
    }
    if (operator.value === 'less_than' && endValue === undefined) {
      return true;
    }
    if (
      operator.value === 'between' &&
      (startValue === undefined || endValue === undefined)
    ) {
      return true;
    }
    return false;
  })();

  const handleAddFilter = () => {
    const { min, max } = (() => {
      switch (operator.value) {
        case 'between':
          return { min: startValue, max: endValue };
        case 'greater_than':
          return { min: startValue, max: undefined };
        case 'less_than':
          return { min: undefined, max: endValue };
        default:
          return { min: startValue, max: endValue };
      }
    })();

    const convertedMin = (() => {
      if (isPayFilter && min !== undefined) {
        return Math.round(min / exchangeRate);
      }
      if (isFundingFilter && min !== undefined) {
        return min / exchangeRate;
      }
      return min;
    })();
    const convertedMax = (() => {
      if (isPayFilter && max !== undefined) {
        return Math.round(max / exchangeRate);
      }
      if (isFundingFilter && max !== undefined) {
        return max / exchangeRate;
      }
      return max;
    })();

    onSave({
      ...filter,
      min: convertedMin,
      max: convertedMax,
      isActive: true,
    });
    closeMenu();
  };

  const handleClearSelections = () => {
    trackFilterReset(filter, {
      selectionCountBefore:
        startValue !== undefined || endValue !== undefined ? 1 : 0,
    });
    setOperator(operatorOptions[0]);
    setStartValue(undefined);
    setEndValue(undefined);
    setShowErrors(false);
  };

  return (
    <div className={layout.formStack}>
      <SubFilterInputForm
        filterId={filter.id}
        startValue={startValue}
        setStartValue={setStartValue}
        endValue={endValue}
        setEndValue={setEndValue}
        operator={operator}
        setOperator={handleOperatorChange}
        errorMessages={errorMessages}
        showErrors={showErrors}
        onShowErrorsChange={setShowErrors}
      />

      <SelectionListControls
        onClear={handleClearSelections}
        onClose={closeMenu}
        onSubmit={() => {
          if (errorMessages.length > 0) {
            setShowErrors(true);
            return;
          }
          handleAddFilter();
        }}
        submitText={filter.isUserSubmitted ? 'Update' : 'Add'}
        clearText="Clear"
        isSubmitDisabled={isAddDisabled}
      />
    </div>
  );
};
